import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LANGUAGE_LABELS,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/i18n";
import { useLanguageSync } from "@/hooks/useLanguageSync";
import { cn } from "@/lib/utils";
import { FlagIcon } from "@/components/FlagIcon";

interface LanguageSwitcherProps {
  /** Render as a compact icon-only trigger (default: true). */
  compact?: boolean;
  className?: string;
}

export function LanguageSwitcher({ compact = true, className }: LanguageSwitcherProps) {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguageSync();

  const handleSelect = async (lang: SupportedLanguage) => {
    if (lang === language) return;
    await changeLanguage(lang);
    toast.success(t("language.changed", { lang: LANGUAGE_LABELS[lang] }));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? "icon" : "sm"}
          className={cn(
            "text-muted-foreground hover:text-foreground",
            compact ? "h-8 w-8" : "gap-2",
            className,
          )}
          aria-label={t("language.label")}
          title={t("language.label")}
        >
          {compact ? (
            <FlagIcon lang={language} className="h-4 w-6" title={LANGUAGE_LABELS[language]} />
          ) : (
            <>
              <FlagIcon lang={language} className="h-4 w-6" title={LANGUAGE_LABELS[language]} />
              <span className="text-xs">{LANGUAGE_LABELS[language]}</span>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>{t("language.label")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onSelect={() => handleSelect(lang)}
            className="flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2">
              <FlagIcon lang={lang} className="h-4 w-6" title={LANGUAGE_LABELS[lang]} />
              <span>{LANGUAGE_LABELS[lang]}</span>
            </span>
            {language === lang && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
